import { useQuery } from 'react-query';
import { useJwtToken, API_ENDPOINT } from 'helper';
import { transformChartPoints } from 'components/DeviceDetails/helper';

export const fetchChartData = async (
    accessToken,
    snKey,
    startTimestamp,
    endTimestamp,
    useRawData
) => {
    const url = new URL(`${API_ENDPOINT}/devices/${snKey}/chart-data`);
    url.searchParams.append('Device_SN_Type', snKey);
    url.searchParams.append('Start_Date', startTimestamp);
    url.searchParams.append('End_Date', endTimestamp);
    url.searchParams.append('Day_View_Raw_Data', useRawData);

    try {
        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: {
                Authorization: accessToken,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data.Items.map((item) => transformChartPoints(item));
    } catch (error) {
        console.error('Error fetching chart data:', error);
        throw error;
    }
};

export const useChartData = (snKey, timestamps, useRawData) => {
    const accessToken = useJwtToken();
    const { startTimestamp, endTimestamp } = timestamps;
    return useQuery(
        ['chartData', snKey, startTimestamp, endTimestamp, useRawData],
        () => {
            return fetchChartData(
                accessToken,
                snKey,
                startTimestamp,
                endTimestamp,
                useRawData
            );
        },
        {
            enabled: !!accessToken,
            staleTime: 5 * 60 * 1000,
            keepPreviousData: true,
        }
    );
};

export const fetchChartAvailability = async (accessToken, serialNumber) => {
    const url = new URL(`${API_ENDPOINT}/devices/${serialNumber}/chart-data/availability`);

    try {
        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: {
                Authorization: accessToken,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data.dates;
    } catch (error) {
        console.error('Error fetching chart availability:', error);
        throw error;
    }
};

export const useChartAvailability = (serialNumber) => {
    const accessToken = useJwtToken();
    return useQuery(
        ['chartAvailability', serialNumber],
        () => fetchChartAvailability(accessToken, serialNumber),
        {
            enabled: !!accessToken && !!serialNumber,
            staleTime: Infinity,
        }
    );
};

export const generateStatsReport = async (accessToken, requestObject) => {
    const url = new URL(
        `${API_ENDPOINT}/devices/${requestObject?.Thing_ID}/generate-report`
    );
    const reportViewBackendMapping = {
        day: 'daily',
        week: 'weekly',
        month: 'monthly',
        year: 'yearly',
    };
    requestObject.Report_View =
        reportViewBackendMapping[requestObject.Report_View] || 'daily';
    try {
        if (!accessToken) {
            console.error('Access token is not available');
            throw new Error('Access token is required to generate report');
        }

        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: {
                Authorization: accessToken,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestObject),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response;
    } catch (error) {
        console.error('Error generating stats report:', error);
        throw error;
    }
};
